import React, { useState, useMemo } from 'react';
import { Autocomplete, TextField, CircularProgress } from '@mui/material';
import { useInfiniteQuery } from '@tanstack/react-query';
import { getApiPlants } from '@/api/gen/api';
import { useDebounce } from './useDebounce';

// minimal plant-like shape accepted by this selector
export interface PlantOption {
  id: number;
  plantName: string;
  // allow extra fields from API types
  [key: string]: any;
}

interface PlantSelectorProps {
  value?: number | null; // plantId
  onChange: (plantId: number | null) => void;
  placeholder?: string;
  disabled?: boolean;
  required?: boolean;
  label?: string;
}

export default function PlantSelector2({
  value,
  onChange,
  placeholder = 'Selecciona una planta',
  disabled = false,
  required = false,
  label = 'Planta',
}: PlantSelectorProps) {
  const [inputValue, setInputValue] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const debouncedSearch = useDebounce(searchQuery, 300);

  const {
    data,
    isLoading,
    error,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
  } = useInfiniteQuery({
    queryKey: ['plants', 'infinite', debouncedSearch],
    queryFn: ({ pageParam }) =>
      getApiPlants({
        isPaginated: true,
        SearchString: debouncedSearch || undefined,
        page: pageParam as number,
        pageSize: 20,
      }).then((response: any) => response),
    getNextPageParam: (lastPage, allPages) => {
      const loaded = allPages.reduce((acc, p) => acc + p.items.length, 0);
      if (loaded < (lastPage.totalCount || 0)) {
        return (lastPage.page || 1) + 1;
      }
      return undefined;
    },
    initialPageParam: 1,
  });

  // If parent passed a value (plantId) that's not in the current pages, fetch it
  // Note: For now, no fallback, assuming selected is in pages

  const options = useMemo(() => {
    const allOpts = (data?.pages?.flatMap(page => page?.items || []) ||
      []) as PlantOption[];
    // Ensure uniqueness by id
    const uniqueOpts = [...new Map(allOpts.map(opt => [opt.id, opt])).values()];
    // Always include the selected plant if it exists and is not already in the list
    const selectedPlant = allOpts.find(plant => plant.id === value);
    if (value && selectedPlant && !uniqueOpts.some(opt => opt.id === value)) {
      uniqueOpts.unshift(selectedPlant);
    }
    return uniqueOpts;
  }, [data, value]);

  // Find the selected plant based on plantId
  const selectedPlant = useMemo(() => {
    return options.find(plant => plant.id === value) || null;
  }, [options, value]);

  const handleScroll = (event: React.SyntheticEvent) => {
    const listboxNode = event.currentTarget;
    if (
      listboxNode.scrollTop + listboxNode.clientHeight >=
        listboxNode.scrollHeight - 50 &&
      hasNextPage &&
      !isFetchingNextPage
    ) {
      fetchNextPage();
    }
  };

  const renderOptionFn = (props: any, option: PlantOption) => {
    const { key, ...other } = props;
    return (
      <li key={key} {...other}>
        {option.plantName}
      </li>
    );
  };

  return (
    <Autocomplete
      disablePortal
      freeSolo
      value={selectedPlant}
      inputValue={inputValue}
      onChange={(_, newValue) => {
        if (typeof newValue === 'object' && newValue !== null) {
          onChange(newValue.id);
        } else {
          // If string or null, do nothing or deselect if needed
          onChange(null);
        }
      }}
      onInputChange={(_, newInputValue, reason) => {
        setInputValue(newInputValue);
        if (reason === 'input') {
          setSearchQuery(newInputValue);
          if (value) {
            onChange(null);
          }
        }
      }}
      options={options}
      getOptionLabel={option =>
        typeof option === 'string' ? option : option.plantName
      }
      isOptionEqualToValue={(option, value) => option.id === value?.id}
      loading={isLoading}
      disabled={disabled}
      renderInput={params => (
        <TextField
          {...params}
          label={label}
          placeholder={placeholder}
          required={required}
          error={!!error}
          helperText={error ? 'Error cargando plantas' : undefined}
          InputProps={{
            ...params.InputProps,
            endAdornment: (
              <>
                {isLoading ? (
                  <CircularProgress color='inherit' size={20} />
                ) : null}
                {params.InputProps.endAdornment}
              </>
            ),
          }}
        />
      )}
      renderOption={renderOptionFn}
      ListboxProps={{
        onScroll: handleScroll,
      }}
      noOptionsText={isLoading ? 'Cargando...' : 'No hay opciones'}
    />
  );
}
